import { ProductSupplierInventoryReceiptDoc } from '@gooduncles/gu-app-schema';
import { chunk } from 'lodash-es';
import useSWR from 'swr';

import {
  getProductInboundTransactions,
  getProductSupplierInventoryReceipts,
} from 'src/utils/firebase/firebase-short-cut';

import { FIRESTORE_IN_QUERY_LIMIT, fetchInventoryById, getTransactionCost } from './useSWRProductSupplierReceiptRows';

export interface ProductSupplierReceiptDateRow {
  id: string;
  transactionDate: string;
  totalPrice: number;
  /** 특이사항(checkList)을 한 줄로 합친 값 */
  remarks: string;
}

const fetchRows = async (supplierId: string): Promise<ProductSupplierReceiptDateRow[]> => {
  const receipts: ProductSupplierInventoryReceiptDoc[] = await getProductSupplierInventoryReceipts([
    ['supplierId', '==', supplierId],
  ]);
  if (receipts.length === 0) return [];

  // 입고서에 달린 입고 처리는 referenceId로 역조회한다. (입고서 30건씩 묶어서 조회)
  const transactions = (
    await Promise.all(
      chunk(
        receipts.map((receipt) => receipt._id),
        FIRESTORE_IN_QUERY_LIMIT,
      ).map((ids) => getProductInboundTransactions([['referenceId', 'in', ids]])),
    )
  ).flat();
  const inventoryById = await fetchInventoryById(transactions);

  const totalPriceByReceiptId = transactions.reduce((acc, transaction) => {
    const cost = getTransactionCost(transaction, inventoryById.get(transaction.inventoryId));
    const totalPrice = cost === null ? 0 : cost * transaction.requestValue;
    return acc.set(transaction.referenceId, (acc.get(transaction.referenceId) ?? 0) + totalPrice);
  }, new Map<string, number>());

  return receipts
    .map((receipt) => ({
      id: receipt._id,
      transactionDate: receipt.transactionDate,
      totalPrice: totalPriceByReceiptId.get(receipt._id) ?? 0,
      remarks: receipt.checkList.map((item) => item.content).join(' / '),
    }))
    .sort((a, b) => b.transactionDate.localeCompare(a.transactionDate));
};

/** 매입처의 입고서를 최신 일자순으로, 일자별 매입금액과 함께 조회한다. */
const useSWRProductSupplierReceiptDates = (supplierId: string | null) => {
  const { data, isLoading, error, mutate } = useSWR(
    supplierId ? `productSupplierReceiptDates-${supplierId}` : null,
    () => fetchRows(supplierId as string),
  );

  return {
    rows: data ?? [],
    isLoading,
    error,
    refresh: () => mutate(),
  };
};

export default useSWRProductSupplierReceiptDates;
